// components/portal/BrandQualificationPanel.tsx
'use client';
import React from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { FaterStampIcon } from '@/components/ui/FaterStampIcon';
import { ChevronRight } from '@/components/ui/lucide-memo';
import { ScoreGroup, ScoreRow } from '@/components/ui/ScoreRows';

const flagLabel = (reason: string, fr: boolean) => ({
  non_retail_activity: fr ? 'Activité non commerciale pour un centre (bureau, automobile, banque…)' : 'Not a retail activity for a centre (office, automotive, bank…)',
  two_years_of_losses: fr ? 'Deux exercices en perte' : 'Two years of losses',
  financial_watchlist: fr ? 'Note financière sous surveillance' : 'Financial rating on watchlist',
  court_proceeding_last_5_years: fr ? 'Procédure collective ces 5 dernières années' : 'Court proceeding in the last 5 years',
  network_shrinking: fr ? 'Réseau en contraction (plus de 10 % en 3 ans)' : 'Network shrinking (over 10% in 3 years)',
} as Record<string, string>)[reason] ?? reason.replaceAll('_', ' ');

// Disclosures read as the app's own rows: a chevron that turns when open, not the browser's triangle.
const SUMMARY = 'flex w-fit cursor-pointer list-none items-center gap-1.5 text-[#AFAFAF] transition-colors hover:text-white [&::-webkit-details-marker]:hidden';
const CHEVRON = 'shrink-0 transition-transform duration-200 [details[open]>summary>&]:rotate-90';

type Component = { known: boolean; points: number; maximum: number; reason: string; source?: string };

// A theme of the Fater score: its subtotal, then one shared score row per criterion.
const CriteriaGroup = ({ title, rows }: { title: string; rows: Array<{ key: string; label: string; component: Component }> }) => {
  const fr = useKernelStore(state => state.language) === 'fr';
  if (rows.length === 0) return null;
  return (
    <ScoreGroup title={title}
      earned={rows.reduce((sum, row) => sum + (row.component.known ? row.component.points : 0), 0)}
      maximum={rows.reduce((sum, row) => sum + row.component.maximum, 0)}>
      {rows.map(({ key, label, component }) => (
        <ScoreRow key={key} label={label} earned={component.known ? component.points : null} maximum={component.maximum}
          reason={component.reason} source={component.source} missingLabel={fr ? 'Non documenté' : 'Not evidenced'} />
      ))}
    </ScoreGroup>
  );
};

const STAMP_GROUPS: Array<{ en: string; fr: string; keys: string[] | null }> = [
  { en: 'Financial health', fr: 'Santé financière', keys: ['financial', 'covenant'] },
  { en: 'Momentum', fr: 'Dynamique', keys: ['aura', 'recency', 'growth'] },
  { en: 'Presence in France', fr: 'Présence en France', keys: ['reach', 'draw', 'frenchHeadquarters'] },
  { en: 'Other', fr: 'Autres', keys: null },
];
const GROUPED = new Set(STAMP_GROUPS.flatMap(group => group.keys ?? []));

/** The brand's Fater score, said first on its Overview: the seal when Fater recommends, the score, and any red flag. */
export const BrandStampHeadline = ({ brandId }: { brandId: string }) => {
  const stamp = useKernelStore(state => state.inventory.Brand.registry[brandId]?.qualification?.stamp);
  const fr = useKernelStore(state => state.language) === 'fr';
  if (!stamp) return null;
  const verdict = stamp.recommended
    ? (fr ? 'Tamponnée par Fater.' : 'Stamped by Fater.')
    : stamp.eligible ? (fr ? 'Notée, pas recommandée.' : 'Scored, not recommended.') : (fr ? 'Hors marché.' : 'Out of market.');
  return (
    <section aria-label={fr ? 'Score Fater' : 'Fater score'} className="flex items-center gap-4">
      {stamp.recommended && <FaterStampIcon size={56} />}
      <div className="min-w-0">
        <h3 className="font-normal text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '18px' }}>{fr ? 'Score Fater' : 'Fater score'}</h3>
        <p className="tabular-nums text-[#8E8E8E]" style={{ fontSize: 15, lineHeight: '36px' }}>
          <span className="font-semibold text-white" style={{ fontSize: 32 }}>{stamp.eligible ? Math.round(stamp.score) : '-'}</span>
          {stamp.eligible ? '/100' : ''} · {fr ? 'preuves' : 'evidence'} {stamp.coverage}%
        </p>
        <p className="text-[#D0D0D0]" style={{ fontSize: 14, lineHeight: '20px' }}>{verdict}</p>
        {stamp.reasons.length > 0 && <ul className="mt-2 space-y-1 text-[#F28B82]" style={{ fontSize: 14, lineHeight: '20px' }}>{stamp.reasons.map(reason => <li key={reason}>{flagLabel(reason, fr)}</li>)}</ul>}
      </div>
    </section>
  );
};

/** The Scoring tab of a brand in Data: every criterion of the Fater score, then the web evidence check. */
export const BrandQualificationPanel = ({ brandId }: { brandId: string }) => {
  const quality = useKernelStore(state => state.inventory.Brand.registry[brandId]?.qualification);
  const lang = useKernelStore(state => state.language);
  const fr = lang === 'fr';
  if (!quality) return <p className="p-6 text-[#8E8E8E]" style={{ fontSize: 14 }}>{fr ? 'Évaluation indisponible. Rechargez la liste.' : 'Assessment unavailable. Reload the list.'}</p>;
  const labels = {
    openingRecency: fr ? 'Ouvertures récentes' : 'Recent openings',
    netGrowth: fr ? 'Croissance nette' : 'Net growth',
    attractiveness: fr ? 'Attractivité' : 'Attractiveness',
    resilience: fr ? 'Résilience financière' : 'Financial resilience',
    frenchHeadquarters: fr ? 'Siège de la marque en France' : 'French brand headquarters',
  };
  const stamp = quality.stamp;
  const stampLabels = {
    client: fr ? 'Intérêt pour URW et Altarea' : 'Fit for URW and Altarea',
    financial: fr ? 'Solidité financière' : 'Financial solidity',
    aura: fr ? 'Dynamique du moment' : 'Market momentum',
    covenant: fr ? 'Surface financière et capacité locative' : 'Covenant and rent capacity',
    recency: fr ? 'Ouvertures récentes' : 'Recent openings',
    growth: fr ? 'Croissance nette du réseau' : 'Net network growth',
    reach: fr ? 'Réseau en France' : 'French network',
    draw: fr ? 'Pouvoir d’attraction du secteur' : 'Sector drawing power',
    frenchHeadquarters: fr ? 'Siège en France' : 'French head office',
  };
  const heading = 'font-semibold text-[#ECECEC]';
  return (
    <div className="space-y-8 py-4">
    {stamp && <section aria-label={fr ? 'Score Fater' : 'Fater score'}>
      {/* The same header as the Overview, so switching tabs moves nothing. */}
      <BrandStampHeadline brandId={brandId} />
      <div className="mt-8 space-y-6">
        {STAMP_GROUPS.map(group => (
          <CriteriaGroup key={group.en} title={fr ? group.fr : group.en}
            rows={Object.entries(stamp.components).filter(([key]) => group.keys === null ? !GROUPED.has(key) : group.keys.includes(key))
              .map(([key, component]) => ({ key, label: stampLabels[key as keyof typeof stampLabels] ?? key, component }))} />
        ))}
      </div>
      <details className="mt-6">
        <summary className={SUMMARY} style={{ fontSize: 14, lineHeight: '20px' }}><ChevronRight size={16} strokeWidth={2} className={CHEVRON} />{fr ? 'Comment le score est calculé' : 'How the score works'}</summary>
        <p className="mt-2 text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '19px' }}>{fr ? 'Lecture foncière sur données officielles : comptes déposés, registre SIRENE, procédures collectives, sièges, magasins. Le score classe les marques ; le tampon Fater est posé à la main par un administrateur Fater.' : 'A landlord’s reading of official data: filed accounts, SIRENE register, court proceedings, head offices, stores. The score ranks brands; the Fater stamp is set by hand by a Fater admin.'}</p>
      </details>
    </section>}
    {quality.eligibility !== 'unassessed' && <details>
      <summary className={SUMMARY} style={{ fontSize: 14, lineHeight: '20px' }}><ChevronRight size={16} strokeWidth={2} className={CHEVRON} />{fr ? 'Contrôle web des preuves' : 'Web evidence check'}</summary>
    <section aria-label={fr ? 'Priorité interne' : 'Internal priority'} className="mt-4">
      <h3 className={heading} style={{ fontSize: 16, lineHeight: '24px' }}>{fr ? 'Priorité interne' : 'Internal priority'} · {quality.priority === null ? (fr ? 'Non notée' : 'Not scored') : quality.eligibility === 'qualified' && quality.evidenceCoverage === 0 ? (fr ? 'Vérifiée · pas encore notée' : 'Verified · not yet scored') : `${quality.priority}/100`}</h3>
      <p className="mt-1 text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '19px' }}>{fr ? 'Expansion 40 · Attractivité 30 · Résilience 20 · Siège français 10. Ce score est distinct de la compatibilité avec un actif. La couverture mesure les critères documentés, pas la confiance ni la qualité de la marque.' : 'Expansion 40 · Attractiveness 30 · Resilience 20 · French HQ 10. This score is separate from asset fit. Coverage measures documented criteria, not confidence or brand quality.'}</p>
      <p className="mt-1 text-[#8E8E8E]" style={{ fontSize: 12 }}>{fr ? 'Calculée au' : 'Evaluated at'} {quality.evaluatedAt}{quality.reviewedAt ? ` · ${fr ? 'Revue le' : 'Reviewed'} ${quality.reviewedAt}` : ''}</p>
      {quality.reasons.length > 0 && <ul className="mt-2 space-y-1 text-[#ECECEC]" style={{ fontSize: 14 }}>{quality.reasons.map(reason => <li key={reason}>{reason === 'no_approved_assessment' ? (fr ? 'Aucune évaluation approuvée. Cette marque reste à vérifier.' : 'No approved assessment. This brand still needs review.') : reason.replaceAll('_', ' ')}</li>)}</ul>}
      <div className="mt-4">
        <CriteriaGroup title={fr ? 'Critères' : 'Criteria'} rows={Object.entries(quality.components).map(([key, component]) => ({ key, label: labels[key as keyof typeof labels] ?? key, component }))} />
      </div>
      {quality.sources.length > 0 && <details className="mt-3">
        <summary className={SUMMARY} style={{ fontSize: 14, lineHeight: '20px' }}><ChevronRight size={16} strokeWidth={2} className={CHEVRON} />{fr ? 'Sources de l’évaluation' : 'Assessment sources'} ({quality.sources.length})</summary>
        <ul className="mt-3 space-y-4" style={{ fontSize: 13 }}>{quality.sources.map(source => <li key={source.id}>
          <a className="break-all text-[#ECECEC] underline" href={/^https?:\/\//i.test(source.url) ? source.url : undefined} target="_blank" rel="noopener noreferrer">{source.id} · {source.url}</a>
          <p className="mt-1 whitespace-pre-wrap text-[#8E8E8E]">{source.quote}</p>
          <p className="mt-1 text-[#8E8E8E]" style={{ fontSize: 12 }}>{source.effectiveAt} · {source.scope}</p>
        </li>)}</ul>
      </details>}
    </section>
    </details>}
    </div>
  );
};
